if (!customElements.get('audio-clip-visualized')) {
  class AudioClipVisualized extends HTMLElement {
    constructor() {
      super();

      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas.getContext('2d');
      this.btn = this.querySelector('button');

      // state
      this.audioCtx = null;
      this.audioBuffer = null;
      this.source = null;

      this.isPlaying = false;
      this.isDragging = false;
      this.wasPlaying = false;

      this.visualProgress = null;
      this.pcmData = [];

      this.startTime = 0;
      this.offset = 0;
    }

    connectedCallback() {
      this.updateButton('play');
      this.loadAudio().then(() => this.animate(true));

      this.btn.addEventListener('click', () => this.playButtonClick());

      this.canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
      this.canvas.addEventListener('mousemove', (e) => this.onMouseMove(e));
      this.canvas.addEventListener('mouseup', () => this.onMouseUp());
      this.canvas.addEventListener('mouseleave', () => this.onMouseLeave());
    }

    async loadAudio() {
      this.pcmData = [];
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();

      const response = await fetch(this.dataset.file);
      const arrayBuffer = await response.arrayBuffer();
      this.audioBuffer = await this.audioCtx.decodeAudioData(arrayBuffer);

      this.canvas.width = this.canvas.offsetWidth * window.devicePixelRatio;
      this.canvas.height = this.canvas.offsetHeight * window.devicePixelRatio;
      this.ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

      const rawData = this.audioBuffer.getChannelData(0);
      const samples = 1000;
      const blockSize = Math.floor(rawData.length / samples);

      for (let i = 0; i < samples; i++) {
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(rawData[(i * blockSize) + j]);
        }
        this.pcmData.push(sum / blockSize);
      }
    }

    playAudio() {
      if (!this.audioBuffer) return;

      this.source = this.audioCtx.createBufferSource();
      this.source.buffer = this.audioBuffer;
      this.source.connect(this.audioCtx.destination);

      this.startTime = this.audioCtx.currentTime - this.offset;

      this.source.start(0, this.offset);
      this.isPlaying = true;
      this.offset = 0;

      this.animate();
    }

    pauseAudio() {
      if (this.source) {
        try { this.source.stop(); } catch {}
        this.source = null;
      }

      this.offset = this.audioCtx.currentTime - this.startTime;
      this.isPlaying = false;
    }

    playButtonClick() {
      if (this.isPlaying) {
        this.pauseAudio();
        this.updateButton('play');
      } else {
        this.playAudio();
        this.updateButton('pause');
      }
    }

    getButtonText(type = '') {
      return this.dataset[type + 'Text'] || this.dataset.playText;
    }

    updateButton(status) {
      this.btn.innerText = this.getButtonText(status);

      if (status === 'pause') {
        this.btn.classList.add('playing');
      } else {
        this.btn.classList.remove('playing');
      }
    }

    animate(initialLoad) {
      const isInit = initialLoad === true;
      if (!this.isPlaying && !isInit) return;

      const currentTime = this.audioCtx.currentTime - this.startTime;
      let progress = currentTime / this.audioBuffer.duration;

      if (this.visualProgress !== null) {
        progress = this.visualProgress;
      }

      const width = this.canvas.width / window.devicePixelRatio;
      const height = this.canvas.height / window.devicePixelRatio;
      const barWidth = width / this.pcmData.length;
      const max = Math.max(...this.pcmData);

      this.ctx.clearRect(0, 0, width, height);

      this.pcmData.forEach((val, i) => {
        const x = i * barWidth;
        const h = (val / max) * height;
        const played = (i / this.pcmData.length) <= progress;

        this.ctx.fillStyle = played ? '#fff' : '#444';
        this.ctx.fillRect(x, (height / 2) - (h / 2), barWidth - 1, h);
      });

      const playheadX = progress * width;

      this.ctx.fillStyle = "white";
      this.ctx.fillRect(playheadX - 1, 0, 2, height);

      this.ctx.beginPath();
      this.ctx.arc(playheadX, height / 2, 5, 0, Math.PI * 2);
      this.ctx.fill();

      if (currentTime < this.audioBuffer.duration && !isInit) {
        requestAnimationFrame(() => this.animate());
      } else {
        this.isPlaying = false;
        if (!isInit) this.updateButton('replay');
      }
    }

    getOffsetFromEvent(e) {
      const rect = this.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      return (x / rect.width) * this.audioBuffer.duration;
    }

    seek(e) {
      this.offset = this.getOffsetFromEvent(e);
      this.offset = Math.max(0, Math.min(this.offset, this.audioBuffer.duration));

      this.visualProgress = this.offset / this.audioBuffer.duration;
      this.startTime = this.audioCtx.currentTime - this.offset;

      this.updateButton(this.wasPlaying ? 'pause' : 'play');
    }

    onMouseDown(e) {
      this.isDragging = true;

      this.wasPlaying = this.isPlaying;
      if (this.isPlaying) this.pauseAudio();

      this.seek(e);
      this.animate(true);
    }

    onMouseMove(e) {
      if (!this.isDragging) return;
      this.seek(e);
      this.animate(true);
    }

    onMouseUp() {
      this.isDragging = false;
      this.visualProgress = null;

      if (this.wasPlaying) this.playAudio();
    }

    onMouseLeave() {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.visualProgress = null;

      if (this.wasPlaying) this.playAudio();
    }
  }

  customElements.define('audio-clip-visualized', AudioClipVisualized);
}